import { useEffect, useState } from 'react';
import { getFreeTrialStatus } from '../../../api/utils/free-trial/api';
import {
  FreeTrialStatusItem,
  StatusEnum,
} from '../../../constants/types/free-trial/type';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import { getErrorMessages } from '../../../api/utils/util-func';
import ActiveTrialFloatingBar from './ActiveFreeTrialFloatingBar';
import CompletedTrialFloatingBar from './CompletedFreeTrialFloatingBar';
import DefaultTrialFloatingBar from './DefaultFreeTrialFloatingBar';

type FloatingBarMode = 'DEFAULT' | 'ACTIVE' | 'COMPLETED';

const getFloatingBarMode = (status: StatusEnum): FloatingBarMode => {
  switch (status) {
    case StatusEnum.APPLIED:
    case StatusEnum.IN_PROGRESS:
      return 'ACTIVE';

    case StatusEnum.COMPLETED:
      return 'COMPLETED';

    default:
      return 'DEFAULT';
  }
};

const FloatingBar = () => {
  const dispatch = useAppDispatch();

  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  const [freeTrialStatus, setFreeTrialStatus] =
    useState<FreeTrialStatusItem | null>(null);

  /**
   * 페이지 진입 후 1.5초 뒤 Floating Bar 표시
   */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  /**
   * 스크롤 중 compact mode 적용
   *
   * 마지막 scroll 이벤트 이후 300ms 동안
   * 추가 스크롤이 없으면 원래 형태로 복귀
   */
  useEffect(() => {
    let scrollTimer: ReturnType<typeof setTimeout>;

    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

    const handleScroll = () => {
      if (!isDesktop()) {
        setIsScrolling(false);
        return;
      }

      setIsScrolling(true);

      window.clearTimeout(scrollTimer);

      scrollTimer = setTimeout(() => {
        setIsScrolling(false);
      }, 300);
    };

    const handleResize = () => {
      if (!isDesktop()) {
        window.clearTimeout(scrollTimer);
        setIsScrolling(false);
      }
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(scrollTimer);
    };
  }, []);

  useEffect(() => {
    const footer = document.querySelector('footer');

    if (!footer) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      },
    );

    observer.observe(footer);

    return () => {
      observer.disconnect();
    };
  }, []);

  const floatingBarVisible = isVisible && !isFooterVisible;

  useEffect(() => {
    const fetchFreeTrialStatus = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');

        if (!accessToken) {
          return;
        }

        const data = await getFreeTrialStatus();

        setFreeTrialStatus(data);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      }
    };

    void fetchFreeTrialStatus();
  }, []);

  const mode: FloatingBarMode | null = freeTrialStatus
    ? getFloatingBarMode(freeTrialStatus.status)
    : 'DEFAULT';

  if (!mode) {
    return null;
  }

  switch (mode) {
    case 'ACTIVE':
      return (
        <ActiveTrialFloatingBar
          isVisible={floatingBarVisible}
          isScrolling={isScrolling}
          freeTrialStatus={freeTrialStatus}
        />
      );

    case 'COMPLETED':
      return (
        <CompletedTrialFloatingBar
          isVisible={floatingBarVisible}
          isScrolling={isScrolling}
          freeTrialStatus={freeTrialStatus}
        />
      );

    default:
      return (
        <DefaultTrialFloatingBar
          isVisible={floatingBarVisible}
          isScrolling={isScrolling}
        />
      );
  }
};

export default FloatingBar;
